import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Placeholder,
  Spinner,
  Dropdown,
  SplitButton,
} from "react-bootstrap";
import { HeadPage } from "src/componentes";
import NFeVisualizaCode from "./NFe.Visualiza.Code";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faPrint,
  faCopy,
  faTruck,
  faBoxes,
  faFileInvoice,
  faDollarSign,
  faInfoCircle,
  faUser,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";

const NFeVisualizaView = () => {
  const code = NFeVisualizaCode();
  const nfe = code.nfe;

  if (code.loading) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar NF-e" />
        <Placeholder animation="glow" className="mt-4">
          <Placeholder xs={12} size="lg" className="mb-3" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
        </Placeholder>
      </Container>
    );
  }

  if (!nfe) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar NF-e" />
        <Card className="mt-4 text-center p-5 border shadow-sm">
          <Card.Body>
            <h4 className="text-body-secondary mb-3">NF-e não encontrada</h4>
            <p className="text-body-secondary mb-4">
              Não foi possível carregar as informações desta Nota Fiscal Eletrônica.
            </p>
            <Button variant="outline-primary" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar para Listagem
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const isAutorizado = nfe.autorizacao?.codigoStatusResposta === 100;
  const chaveAcesso =
    (nfe.autorizacao as any)?.chaveAcesso ||
    (nfe as any).chaveAcesso ||
    "";
  const protocolo =
    (nfe.autorizacao as any)?.numeroProtocolo ||
    (nfe.autorizacao as any)?.protocolo ||
    "";

  return (
    <Container className="pb-5 container-print">
      {/* Cabeçalho superior da página */}
      <div className="no-print">
        <HeadPage headText={`NF-e: ${nfe.numeroNF || "Nova"} / Série: ${nfe.serie || "-"}`} />

        {/* Barra de Ações Rápidas */}
        <Card className="my-3 border shadow-sm">
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3">
            <Button variant="outline-secondary" size="sm" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onBaixarXml}
                disabled={code.downloadingXml || !nfe.id}
                title="Baixar XML da NF-e"
              >
                {code.downloadingXml ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                )}
                Baixar XML
              </Button>
              <SplitButton
                variant="outline-primary"
                size="sm"
                title={
                  <>
                    {code.printing ? (
                      <Spinner size="sm" animation="border" className="me-1" />
                    ) : (
                      <FontAwesomeIcon icon={faPrint} className="me-2" />
                    )}
                    Imprimir DANFE
                  </>
                }
                onClick={code.onImprimir}
                disabled={code.printing || !nfe.id}
              >
                <Dropdown.Item onClick={code.onImprimir}>
                  Imprimir agora
                </Dropdown.Item>
                <Dropdown.Item onClick={code.onCompartilharDanfe}>
                  Compartilhar
                </Dropdown.Item>
                <Dropdown.Item onClick={code.onBaixarDanfe}>
                  Baixar DANFE
                </Dropdown.Item>
              </SplitButton>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Situação e Autorização SEFAZ */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2 d-flex justify-content-between align-items-center">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            Situação / Autorização SEFAZ
          </div>
          <Badge bg={isAutorizado ? "success" : "warning"} className={!isAutorizado ? "text-dark" : ""}>
            {nfe.autorizacao?.codigoStatusResposta
              ? `${nfe.autorizacao.codigoStatusResposta} - ${nfe.autorizacao.descricaoMotivoResposta}`
              : nfe.autorizacao?.descricaoStatusEnvio || "Não transmitida"}
          </Badge>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {chaveAcesso && (
              <Col xs={12}>
                <small className="text-body-secondary d-block">Chave de Acesso</small>
                <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                  <span className="font-monospace fs-6 text-body fw-bold user-select-all">
                    {chaveAcesso}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="py-0 px-2"
                    onClick={() => code.copiarTexto(chaveAcesso, "Chave de Acesso")}
                    title="Copiar Chave de Acesso"
                  >
                    <FontAwesomeIcon icon={faCopy} className="me-1" />
                    Copiar
                  </Button>
                </div>
              </Col>
            )}

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">Status Envio</small>
              <span className="fw-semibold text-body">
                {nfe.autorizacao?.codigoStatusEnvio != null
                  ? `${nfe.autorizacao.codigoStatusEnvio} - ${nfe.autorizacao.descricaoStatusEnvio || ""}`
                  : "-"}
              </span>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">Status Resposta</small>
              <span className="fw-semibold text-body">
                {nfe.autorizacao?.codigoStatusResposta != null
                  ? `${nfe.autorizacao.codigoStatusResposta} - ${nfe.autorizacao.descricaoMotivoResposta || ""}`
                  : "-"}
              </span>
            </Col>

            {protocolo && (
              <Col xs={12} sm={6} md={3}>
                <small className="text-body-secondary d-block">Protocolo de Autorização</small>
                <span className="fw-semibold text-body font-monospace">{protocolo}</span>
              </Col>
            )}

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">Data/Hora Emissão</small>
              <span className="fw-semibold text-body">
                {code.FormatarDataHoraDefault(nfe.emissaoDataHora)}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Dados Gerais da NF-e */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Dados Gerais da Operação
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Número</small>
              <span className="fw-bold text-body">{nfe.numeroNF || "-"}</span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Série</small>
              <span className="fw-semibold text-body">{nfe.serie || "-"}</span>
            </Col>
            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">Tipo Operação</small>
              <span className="fw-semibold text-body">
                {code.getTipoOperacaoLabel(nfe.tipoOperacao)}
              </span>
            </Col>
            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">Finalidade</small>
              <span className="fw-semibold text-body">
                {code.getFinalidadeLabel(nfe.finalidadeEmissao)}
              </span>
            </Col>
            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">Destino Operação</small>
              <span className="fw-semibold text-body">
                {code.getDestinoOperacaoLabel(nfe.destinoOperacao)}
              </span>
            </Col>
            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">Consumidor Final</small>
              <span className="fw-semibold text-body">
                {code.getConsumidorFinalLabel(nfe.consumidorFinal)}
              </span>
            </Col>
            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">Tributação</small>
              <span className="fw-semibold text-body">
                {code.tributacaoNome || (nfe.tributacaoId ? `Cód. ${nfe.tributacaoId}` : "-")}
              </span>
            </Col>

            {nfe.saidaEntradaDataHora && (
              <Col xs={12} sm={6} md={3}>
                <small className="text-body-secondary d-block">Data/Hora Saída/Entrada</small>
                <span className="fw-semibold text-body">
                  {code.FormatarDataHoraDefault(nfe.saidaEntradaDataHora)}
                </span>
              </Col>
            )}

            {nfe.referencia && nfe.referencia.length > 0 && (
              <Col xs={12}>
                <small className="text-body-secondary d-block">Documentos Referenciados</small>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {nfe.referencia.map((ref, idx) => (
                    <Badge key={idx} bg="secondary" className="font-monospace p-2">
                      {ref.chaveAcesso}
                    </Badge>
                  ))}
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Destinatário */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Destinatário / Remetente
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <small className="text-body-secondary d-block">Nome / Razão Social</small>
              <span className="fw-bold text-body">
                {nfe.destinatario?.nome || "Não informado"}
              </span>
              {nfe.destinatario?.nomeFantasia && (
                <div className="text-body-secondary small">
                  Fantasia: {nfe.destinatario.nomeFantasia}
                </div>
              )}
            </Col>

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">CPF / CNPJ</small>
              <span className="fw-semibold text-body">
                {code.formatarDocumento(nfe.destinatario?.documento)}
              </span>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">Inscrição Estadual</small>
              <span className="fw-semibold text-body">
                {nfe.destinatario?.inscricaoEstadual || "Isento / Não informado"}
              </span>
            </Col>

            <Col xs={12} md={6}>
              <small className="text-body-secondary d-block">Endereço</small>
              <span className="text-body">
                {nfe.destinatario?.endereco
                  ? `${nfe.destinatario.endereco}${
                      nfe.destinatario.enderecoNumero
                        ? `, ${nfe.destinatario.enderecoNumero}`
                        : ""
                    }${
                      nfe.destinatario.enderecoComplemento
                        ? ` - ${nfe.destinatario.enderecoComplemento}`
                        : ""
                    }`
                  : "-"}
              </span>
            </Col>

            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Bairro</small>
              <span className="text-body">{nfe.destinatario?.bairro || "-"}</span>
            </Col>

            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Município / UF</small>
              <span className="text-body">
                {nfe.destinatario?.nomeMunicipio
                  ? `${nfe.destinatario.nomeMunicipio} - ${nfe.destinatario.uf || ""}`
                  : nfe.destinatario?.uf || "-"}
              </span>
            </Col>

            <Col xs={12} sm={4} md={2}>
              <small className="text-body-secondary d-block">CEP</small>
              <span className="text-body">
                {nfe.destinatario?.cep ? code.FormatarCEP(nfe.destinatario.cep) : "-"}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Itens / Produtos */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2 d-flex justify-content-between align-items-center">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faBoxes} className="me-2" />
            Produtos e Serviços ({code.itens.length})
          </div>
          <small className="text-body-secondary">Clique no item para ver impostos</small>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}></th>
                <th style={{ width: "50px" }}>Seq</th>
                <th style={{ width: "110px" }}>Código</th>
                <th>Descrição do Produto</th>
                <th style={{ width: "90px" }}>NCM</th>
                <th style={{ width: "70px" }}>CFOP</th>
                <th style={{ width: "60px" }}>UN</th>
                <th className="text-end" style={{ width: "90px" }}>Qtd</th>
                <th className="text-end" style={{ width: "110px" }}>Vlr. Unit</th>
                <th className="text-end" style={{ width: "120px" }}>Vlr. Total</th>
              </tr>
            </thead>
            <tbody>
              {code.itens.length > 0 ? (
                code.itens.map((item, index) => {
                  const isExpanded = code.expandedItemIndex === index;
                  return (
                    <React.Fragment key={index}>
                      <tr
                        onClick={() => code.toggleItemExpansion(index)}
                        style={{ cursor: "pointer" }}
                        className={isExpanded ? "table-active" : ""}
                      >
                        <td className="text-center text-body-secondary">
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            size="sm"
                          />
                        </td>
                        <td>{item.sequencia || index + 1}</td>
                        <td className="font-monospace small">{item.codigoProduto || "-"}</td>
                        <td>
                          <div className="fw-semibold text-body">{item.nome}</div>
                          {item.descricao && (
                            <small className="text-body-secondary d-block">
                              {item.descricao}
                            </small>
                          )}
                        </td>
                        <td className="font-monospace small">{item.codigoNCM || "-"}</td>
                        <td className="font-monospace small">{item.cfop || "-"}</td>
                        <td>{item.unidadeComercial || "UN"}</td>
                        <td className="text-end">
                          {Number(item.quantidadeComercial || 0).toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td className="text-end">
                          {code.formatarMoeda(item.valorUnitarioComercial)}
                        </td>
                        <td className="text-end fw-bold text-body">
                          {code.formatarMoeda(item.valorTotal)}
                        </td>
                      </tr>

                      {/* Detalhes de Impostos Expandidos */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <div className="p-3 bg-body-tertiary border-top border-bottom">
                              <h6 className="text-primary mb-2 fw-semibold">
                                Tributos do Item {item.sequencia || index + 1}
                              </h6>
                              <Row className="g-3">
                                {/* ICMS */}
                                <Col xs={12} md={4}>
                                  <div className="p-2 border rounded bg-body">
                                    <span className="fw-semibold text-body d-block border-bottom pb-1 mb-2">
                                      ICMS
                                    </span>
                                    <div className="small">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">CST:</span>
                                        <span className="fw-semibold text-body">
                                          {item.icms?.cst != null ? item.icms.cst : "-"}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Base Cálc.:</span>
                                        <span>{code.formatarMoeda(item.icms?.valorBC)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Alíquota:</span>
                                        <span>{item.icms?.percentualICMS || 0}%</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Valor ICMS:</span>
                                        <span className="fw-bold text-body">
                                          {code.formatarMoeda(item.icms?.valorICMS)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                {/* Descontos / Frete do Item */}
                                <Col xs={12} md={4}>
                                  <div className="p-2 border rounded bg-body">
                                    <span className="fw-semibold text-body d-block border-bottom pb-1 mb-2">
                                      Rateios e Descontos
                                    </span>
                                    <div className="small">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Desconto:</span>
                                        <span>{code.formatarMoeda(item.valorDesconto)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Frete:</span>
                                        <span>{code.formatarMoeda(item.valorFrete)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Seguro:</span>
                                        <span>{code.formatarMoeda(item.valorSeguro)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Outras Desp.:</span>
                                        <span>{code.formatarMoeda(item.outrasOutro)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                {/* Dados Adicionais do Produto */}
                                <Col xs={12} md={4}>
                                  <div className="p-2 border rounded bg-body">
                                    <span className="fw-semibold text-body d-block border-bottom pb-1 mb-2">
                                      Classificação / Barras
                                    </span>
                                    <div className="small">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Código Barras:</span>
                                        <span className="font-monospace">
                                          {item.codigoBarras || "SEM GTIN"}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Categoria:</span>
                                        <span>{item.categoriaNome || "-"}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Origem:</span>
                                        <span>
                                          {item.icms?.origemDescricao ||
                                            (item.icms?.origem != null
                                              ? `Origem ${item.icms.origem}`
                                              : "-")}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              {/* PIS, COFINS e IBS/CBS */}
                              <Row className="g-3 mt-1">
                                {/* PIS */}
                                <Col xs={12} sm={6} md={3}>
                                  <div className="p-2 border rounded bg-body">
                                    <span className="fw-semibold text-body d-block border-bottom pb-1 mb-2">
                                      PIS
                                    </span>
                                    <div className="small">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">CST:</span>
                                        <span className="fw-semibold text-body">
                                          {item.pis?.cst != null ? item.pis.cst : "-"}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Base Cálc.:</span>
                                        <span>{code.formatarMoeda(item.pis?.valorBC)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Alíquota:</span>
                                        <span>{item.pis?.percentualPIS || 0}%</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Valor PIS:</span>
                                        <span className="fw-bold text-body">
                                          {code.formatarMoeda(item.pis?.valorPIS)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                {/* COFINS */}
                                <Col xs={12} sm={6} md={3}>
                                  <div className="p-2 border rounded bg-body">
                                    <span className="fw-semibold text-body d-block border-bottom pb-1 mb-2">
                                      COFINS
                                    </span>
                                    <div className="small">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">CST:</span>
                                        <span className="fw-semibold text-body">
                                          {item.cofins?.cst != null ? item.cofins.cst : "-"}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Base Cálc.:</span>
                                        <span>{code.formatarMoeda(item.cofins?.valorBC)}</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Alíquota:</span>
                                        <span>{item.cofins?.percentualCOFINS || 0}%</span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span className="text-body-secondary">Valor COFINS:</span>
                                        <span className="fw-bold text-body">
                                          {code.formatarMoeda(item.cofins?.valorCOFINS)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                {/* IBS / CBS */}
                                <Col xs={12} md={6}>
                                  <div className="p-2 border rounded bg-body border-info-subtle">
                                    <span className="fw-semibold text-primary d-block border-bottom pb-1 mb-2">
                                      IBS / CBS (Reforma Tributária)
                                    </span>
                                    <div className="small">
                                      <Row className="g-2">
                                        <Col xs={6}>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">CST:</span>
                                            <span className="fw-semibold text-body">
                                              {item.ibscbs?.cst != null ? item.ibscbs.cst : "-"}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">Class. Trib.:</span>
                                            <span className="fw-semibold text-body font-monospace">
                                              {item.ibscbs?.classTrib || item.ibscbs?.cClassifTrib || "-"}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">Base Cálc.:</span>
                                            <span>{code.formatarMoeda(item.ibscbs?.valorBC)}</span>
                                          </div>
                                        </Col>
                                        <Col xs={6}>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">Valor IBS:</span>
                                            <span className="fw-bold text-body">
                                              {code.formatarMoeda(item.ibscbs?.valorIBS)}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">Valor CBS:</span>
                                            <span className="fw-bold text-body">
                                              {code.formatarMoeda(item.ibscbs?.valorCBS)}
                                            </span>
                                          </div>
                                          <div className="d-flex justify-content-between">
                                            <span className="text-body-secondary">IBS UF/Mun:</span>
                                            <span>
                                              {code.formatarMoeda(item.ibscbs?.valorUFIbs)} / {code.formatarMoeda(item.ibscbs?.valorMunicipioIBS)}
                                            </span>
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-body-secondary">
                    Nenhum produto cadastrado nesta NF-e.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Transporte e Volumes */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faTruck} className="me-2" />
            Transporte e Volumes
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <small className="text-body-secondary d-block">Modalidade do Frete</small>
              <span className="fw-semibold text-body">
                {code.getModalidadeFreteLabel(nfe.transporte?.modalidadeFrete)}
              </span>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">Placa do Veículo</small>
              <span className="fw-semibold text-body">
                {nfe.transporte?.placa || "-"}
              </span>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <small className="text-body-secondary d-block">UF da Placa</small>
              <span className="fw-semibold text-body">
                {nfe.transporte?.placaUF || "-"}
              </span>
            </Col>

            {nfe.transporte?.nome && (
              <>
                <Col xs={12} md={6}>
                  <small className="text-body-secondary d-block">Transportadora</small>
                  <span className="text-body">{nfe.transporte.nome}</span>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <small className="text-body-secondary d-block">CNPJ / CPF Transportadora</small>
                  <span className="text-body">
                    {code.formatarDocumento(nfe.transporte.documento)}
                  </span>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <small className="text-body-secondary d-block">Inscrição Estadual</small>
                  <span className="text-body">
                    {nfe.transporte.inscricaoEstadual || "-"}
                  </span>
                </Col>
              </>
            )}

            {/* Volumes */}
            {nfe.transporte?.volume && nfe.transporte.volume.length > 0 && (
              <Col xs={12}>
                <div className="border-top pt-3 mt-2">
                  <span className="fw-semibold text-body d-block mb-2">Volumes Transportados</span>
                  <Table responsive size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Quantidade</th>
                        <th>Espécie</th>
                        <th>Marca</th>
                        <th className="text-end">Peso Líquido (kg)</th>
                        <th className="text-end">Peso Bruto (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nfe.transporte.volume.map((vol, idx) => (
                        <tr key={idx}>
                          <td>{vol.quantidadeVolumes || "-"}</td>
                          <td>{vol.especie || "-"}</td>
                          <td>{vol.marca || "-"}</td>
                          <td className="text-end">
                            {Number(vol.pesoLiquido || 0).toLocaleString("pt-BR", {
                              minimumFractionDigits: 3,
                            })}
                          </td>
                          <td className="text-end">
                            {Number(vol.pesoBruto || 0).toLocaleString("pt-BR", {
                              minimumFractionDigits: 3,
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Pagamentos */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faDollarSign} className="me-2" />
            Pagamentos Realizados
          </div>
        </Card.Header>
        <Card.Body>
          {nfe.pagamento && nfe.pagamento.length > 0 ? (
            <Row className="g-3">
              {nfe.pagamento.map((pag, idx) => (
                <Col xs={12} sm={6} md={4} key={idx}>
                  <div className="p-3 bg-body-tertiary border rounded">
                    <div className="small text-body-secondary mb-1">
                      {code.getMeioPagamentoLabel(
                        pag.meioPagamento,
                        pag.descricaoMeioPagamento
                      )}
                    </div>
                    <div className="h5 mb-0 fw-bold text-body">
                      {code.formatarMoeda(pag.valorPagamento)}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-body-secondary">Nenhum pagamento registrado.</div>
          )}
        </Card.Body>
      </Card>

      {/* Totais da NF-e */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            Totais da NF-e
          </div>
        </Card.Header>
        <Card.Body>
          {/* Linha principal com valores destacados */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6} md={3}>
              <div className="p-3 bg-body-tertiary border rounded text-center">
                <small className="text-body-secondary d-block fw-semibold">VALOR PRODUTOS</small>
                <div className="h5 mb-0 fw-bold text-body mt-1">
                  {code.formatarMoeda(nfe.total?.valorProduto)}
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div className="p-3 bg-body-tertiary border rounded text-center">
                <small className="text-body-secondary d-block fw-semibold">DESCONTOS</small>
                <div className="h5 mb-0 fw-bold text-danger mt-1">
                  - {code.formatarMoeda(nfe.total?.valorDesconto)}
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div className="p-3 bg-body-tertiary border rounded text-center">
                <small className="text-body-secondary d-block fw-semibold">FRETE</small>
                <div className="h5 mb-0 fw-bold text-body mt-1">
                  {code.formatarMoeda(nfe.total?.valorFrete)}
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div className="p-3 bg-body-tertiary border border-primary rounded text-center">
                <small className="text-primary d-block fw-bold">TOTAL DA NOTA</small>
                <div className="h4 mb-0 fw-bold text-primary mt-1">
                  {code.formatarMoeda(nfe.total?.valorNotaFiscal)}
                </div>
              </div>
            </Col>
          </Row>

          {/* Quadro resumido de impostos e outros custos */}
          <Row className="g-3">
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Base Cálc. ICMS</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorBC)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Valor ICMS</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorICMS)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Base ICMS ST</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorBCST)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Valor ICMS ST</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorST)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Valor PIS</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorPis)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Valor COFINS</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorCofins)}
              </span>
            </Col>

            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Valor IPI</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorIPI)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Seguro</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorSeguro)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Outras Despesas</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorOutro)}
              </span>
            </Col>
            <Col xs={6} sm={4} md={2}>
              <small className="text-body-secondary d-block">Troco</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorTroco)}
              </span>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <small className="text-body-secondary d-block">Aprox. Tributos (IBPT)</small>
              <span className="fw-semibold text-body">
                {code.formatarMoeda(nfe.total?.valorAproxTributos)}
              </span>
            </Col>
          </Row>

          {/* Totais IBS / CBS (Reforma Tributária) */}
          {Boolean(
            nfe.totalIBSCBS?.valorIbs ||
            nfe.totalIBSCBS?.valorCbs ||
            nfe.totalIBSCBS?.valorBCIbsCbs
          ) && (
            <div className="mt-3 p-3 border rounded bg-body-tertiary">
              <h6 className="text-primary fw-semibold mb-2">Totais IBS / CBS (Reforma Tributária)</h6>
              <Row className="g-3">
                <Col xs={6} sm={3}>
                  <small className="text-body-secondary d-block">Base Cálc. IBS/CBS</small>
                  <span className="fw-semibold text-body">
                    {code.formatarMoeda(nfe.totalIBSCBS?.valorBCIbsCbs)}
                  </span>
                </Col>
                <Col xs={6} sm={3}>
                  <small className="text-body-secondary d-block">Total IBS</small>
                  <span className="fw-bold text-body">
                    {code.formatarMoeda(nfe.totalIBSCBS?.valorIbs)}
                  </span>
                </Col>
                <Col xs={6} sm={3}>
                  <small className="text-body-secondary d-block">Total CBS</small>
                  <span className="fw-bold text-body">
                    {code.formatarMoeda(nfe.totalIBSCBS?.valorCbs)}
                  </span>
                </Col>
                <Col xs={6} sm={3}>
                  <small className="text-body-secondary d-block">IBS Estadual (UF)</small>
                  <span className="fw-semibold text-body">
                    {code.formatarMoeda(nfe.totalIBSCBS?.valorUFIbs)}
                  </span>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Informações Adicionais */}
      {(nfe.informacoesAdicionais?.informacoesComplementaresContribuinte ||
        nfe.informacoesAdicionais?.informacoesAdicionaisFisco ||
        nfe.informacoesAdicionais?.mensagemTributacao) && (
        <Card className="border shadow-sm mb-4">
          <Card.Header className="bg-body-tertiary py-2">
            <div className="fw-semibold text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Informações Adicionais
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {nfe.informacoesAdicionais.informacoesComplementaresContribuinte && (
                <Col xs={12} md={nfe.informacoesAdicionais.informacoesAdicionaisFisco ? 6 : 12}>
                  <small className="text-body-secondary d-block fw-semibold mb-1">
                    Informações Complementares de Interesse do Contribuinte
                  </small>
                  <div className="p-3 bg-body-tertiary border rounded small text-body font-monospace text-break">
                    {nfe.informacoesAdicionais.informacoesComplementaresContribuinte}
                  </div>
                </Col>
              )}
              {nfe.informacoesAdicionais.informacoesAdicionaisFisco && (
                <Col xs={12} md={nfe.informacoesAdicionais.informacoesComplementaresContribuinte ? 6 : 12}>
                  <small className="text-body-secondary d-block fw-semibold mb-1">
                    Informações Adicionais de Interesse do Fisco
                  </small>
                  <div className="p-3 bg-body-tertiary border rounded small text-body font-monospace text-break">
                    {nfe.informacoesAdicionais.informacoesAdicionaisFisco}
                  </div>
                </Col>
              )}
              {nfe.informacoesAdicionais.mensagemTributacao && (
                <Col xs={12}>
                  <small className="text-body-secondary d-block fw-semibold mb-1">
                    Mensagem da Tributação
                  </small>
                  <div className="p-3 bg-body-tertiary border rounded small text-body font-monospace text-break">
                    {nfe.informacoesAdicionais.mensagemTributacao}
                  </div>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default NFeVisualizaView;
